/**
 * Authentication Utilities
 * 
 * This module provides all authentication-related functions including:
 * - User registration with email and password
 * - User login with email and password
 * - User logout
 * - JWT token generation and verification
 * - Getting current authenticated user
 * 
 * All functions interact with Firebase Authentication and handle JWT token management.
 */

import { db, auth } from '../../../config/firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdToken,
  deleteUser as deleteAuthUser,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  collection,
  getDocs,
  writeBatch,
} from 'firebase/firestore';

/**
 * Register a new user with email and password
 * 
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<Object>} - User object with uid and email
 * @throws {Error} - Firebase auth error if registration fails
 */
export const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      // email: user.email, // Removed for privacy or redundancy
      isAdmin: false,
      createdAt: new Date(),
    });

    // Get JWT token
    const token = await getIdToken(user);
    
    return {
      uid: user.uid,
      email: user.email,
      token,
      success: true,
    };
  } catch (error) {
    console.error('Registration error:', error.message);
    throw new Error(error.message);
  }
};

/**
 * Login user with email and password
 * 
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<Object>} - User object with uid, email, and JWT token
 * @throws {Error} - Firebase auth error if login fails
 */
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Ensure user document exists in Firestore (for backward compatibility)
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        // email: user.email, // Removed for privacy or redundancy
        isAdmin: false,
        createdAt: new Date(),
      });
    }

    // Get JWT token
    const token = await getIdToken(user);
    
    return {
      uid: user.uid,
      email: user.email,
      token,
      success: true,
    };
  } catch (error) {
    console.error('Login error:', error.message);
    throw new Error(error.message);
  }
};

/**
 * Logout the current user
 * 
 * @returns {Promise<void>}
 * @throws {Error} - Firebase auth error if logout fails
 */
export const logoutUser = async () => {
  try {
    await signOut(auth);
    // Clear token from localStorage
    localStorage.removeItem('authToken');
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error.message);
    throw new Error(error.message);
  }
};

/**
 * Get current authenticated user with JWT token
 * 
 * @returns {Promise<Object|null>} - User object with uid, email, and token, or null if not authenticated
 */
export const getCurrentUser = async () => {
  return new Promise((resolve) => {
    let hasResolved = false;
    let unsubscribe = () => {};

    const resolveOnce = (value) => {
      if (hasResolved) return;
      hasResolved = true;
      unsubscribe();
      resolve(value);
    };

    const timer = setTimeout(() => {
      const user = auth.currentUser;
      if (user) {
        resolveOnce({
          uid: user.uid,
          email: user.email,
          token: null,
        });
      } else {
        localStorage.removeItem('authToken');
        resolveOnce(null);
      }
    }, 10000);

    unsubscribe = onAuthStateChanged(auth, async (user) => {
      clearTimeout(timer);
      
      if (hasResolved) {
        return;
      }

      unsubscribe();

      const finish = (value) => {
        hasResolved = true;
        resolve(value);
      };

      if (user) {
        try {
          const token = await getIdToken(user);
          // Store token in localStorage for persistence
          localStorage.setItem('authToken', token);
          finish({
            uid: user.uid,
            email: user.email,
            token,
          });
        } catch (error) {
          console.error('Error getting token:', error.message);
          finish(null);
        }
      } else {
        localStorage.removeItem('authToken');
        finish(null);
      }
    }, () => {
      clearTimeout(timer);
      localStorage.removeItem('authToken');
      resolveOnce(null);
    });
  });
};

/**
 * Get JWT token for current user
 * 
 * @returns {Promise<string|null>} - JWT token or null if not authenticated
 */
export const getAuthToken = async () => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await getIdToken(user);
      return token;
    } catch (error) {
      console.error('Error getting token:', error.message);
      return null;
    }
  }
  return null;
};

/**
 * Check if user is authenticated
 * 
 * @returns {boolean} - True if user is authenticated, false otherwise
 */
export const isUserAuthenticated = () => {
  return auth.currentUser !== null;
};

/**
 * Get current authenticated user's UID
 * 
 * @returns {string|null} - User's UID or null if not authenticated
 */
export const getCurrentUserUID = () => {
  const user = auth.currentUser;
  return user ? user.uid : null;
};

/**
 * Wait for authentication state to initialize
 * 
 * This function returns a promise that resolves with the current user's UID
 * after Firebase has completed its auth state initialization.
 * 
 * @returns {Promise<string|null>} - User's UID after auth initialization
 */
export const waitForAuthStateReady = () => {
  return new Promise((resolve) => {
    let hasResolved = false;
    let unsubscribe = () => {};

    const resolveOnce = (value) => {
      if (hasResolved) return;
      hasResolved = true;
      unsubscribe();
      resolve(value);
    };

    const timer = setTimeout(() => {
      resolveOnce(auth.currentUser ? auth.currentUser.uid : null);
    }, 10000);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timer);
      resolveOnce(user ? user.uid : null);
    }, () => {
      clearTimeout(timer);
      resolveOnce(null);
    });
  });
};

/**
 * Verify if token is valid (exists and can be used)
 * 
 * @param {string} token - JWT token to verify
 * @returns {boolean} - True if token is valid, false otherwise
 */
export const isTokenValid = (token) => {
  try {
    if (!token) return false;
    // Check if token is stored and not empty
    return token.length > 0;
  } catch (error) {
    return false;
  }
};

/**
 * Check if user is an admin
 * 
 * @param {string} userId - ID of the user to check
 * @returns {Promise<boolean>} - True if user is an admin, false otherwise
 */
export const isUserAdmin = async (userId) => {
  try {
    if (!userId) return false;
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data().isAdmin === true;
    }
    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Delete user account and all associated data
 * 
 * @param {string} userId - ID of the user to delete
 * @returns {Promise<Object>} - Success status
 */
export const deleteUserAccount = async (userId) => {
  try {
    const user = auth.currentUser;
    if (!user || user.uid !== userId) {
      throw new Error('Unauthorized or user mismatch');
    }

    // List of collections to delete for this user
    const collectionsToDelete = [
      'draft-portfolios',
      'published-portfolios',
      'images',
      'projects',
      'skills',
      'jobs'
    ];

    // Delete all data in user subcollections
    for (const collectionName of collectionsToDelete) {
      const colRef = collection(db, 'users', userId, collectionName);
      const querySnapshot = await getDocs(colRef);
      
      // Use batches for deletion if there are many docs
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    }

    // Delete the user document itself
    await deleteDoc(doc(db, 'users', userId));

    // Delete the auth user
    await deleteAuthUser(user);

    // Clear local storage
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');

    return { success: true, message: 'Account and data deleted successfully' };
  } catch (error) {
    console.error('Error deleting account:', error.message);
    throw new Error(`Failed to delete account: ${error.message}`);
  }
};
